import { useState, useLayoutEffect } from "react";
import { OnboardingLayout } from "@/components/onboarding/OnboardingLayout";
import { StepBusinessSearch, Business } from "@/components/onboarding/StepBusinessSearch";
import { StepBusinessConfirm } from "@/components/onboarding/StepBusinessConfirm";
import { StepTrainingProgress } from "@/components/onboarding/StepTrainingProgress";
import { StepContactInfo, ContactInfo } from "@/components/onboarding/StepContactInfo";
import { StepCreateAccount } from "@/components/onboarding/StepCreateAccount";
import { StepChoosePlan } from "@/components/onboarding/StepChoosePlan";
import { StepSuccess } from "@/components/onboarding/StepSuccess";
import { businessService } from "@/services/businessService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface OnboardingData {
  selectedBusiness: Business | null;
  contactInfo: ContactInfo | null;
  accountCreated: boolean;
  selectedPlan: string | null;
}

const Onboarding = () => {
  const { toast } = useToast();
  const { signup } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [businessSubstep, setBusinessSubstep] = useState<'search' | 'confirm' | 'training'>('search');
  const [showSuccess, setShowSuccess] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    selectedBusiness: null,
    contactInfo: null,
    accountCreated: false,
    selectedPlan: null,
  });

  const totalSteps = 4;

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("sophie-theme");

    return () => {
      root.classList.remove("sophie-theme");
    };
  }, []);

  const handleBusinessSelect = (business: Business) => {
    setOnboardingData({ ...onboardingData, selectedBusiness: business });
    setBusinessSubstep('confirm');
  };

  const handleBusinessConfirm = () => {
    setBusinessSubstep('training');
  };

  const handleBusinessBack = () => {
    setBusinessSubstep('search');
  };

  const handleTrainingComplete = () => {
    nextStep();
  };

  const handleContactInfo = (contactInfo: ContactInfo) => {
    setOnboardingData({ ...onboardingData, contactInfo });
    nextStep();
  };

  const saveBusinessData = async () => {
      if (!onboardingData.selectedBusiness) return;

      const b = onboardingData.selectedBusiness;
      try {
          // Save basic info
          await businessService.updateBusinessInfo({
              businessName: b.name,
              businessCategory: b.categories[0] || "",
              address: b.address,
              businessPhone: b.phone,
              website: b.website,
              // We can map other fields if we had them in Business object or default them
              about: `Google Rating: ${b.rating} (${b.reviewCount} reviews)`
          });

          // Save hours if available
          if (b.hours) {
              await businessService.updateBusinessHours(b.hours);
          }
      } catch (error) {
          console.error("Failed to save initial business data", error);
          // We don't block progress, just log
          toast({
              title: "Warning",
              description: "Could not auto-save business details. You can enter them later.",
              variant: "destructive"
          });
      }
  };

  const handleAccountCreation = async (accountData: { password: string }) => {
    if (!onboardingData.contactInfo || !onboardingData.selectedBusiness) {
      toast({ 
        title: "Error", 
        description: "Missing information", 
        variant: "destructive" 
      });
      return;
    }

    try {
      const name = `${onboardingData.contactInfo.firstName} ${onboardingData.contactInfo.lastName}`;
      const email = onboardingData.contactInfo.email;
      const businessName = onboardingData.selectedBusiness.name;
      
      // Store pending data to be saved after verification/login
      const pendingData = {
          business: onboardingData.selectedBusiness,
          contact: onboardingData.contactInfo
      };
      localStorage.setItem('sophie_pending_business_data', JSON.stringify(pendingData));
      localStorage.setItem('isSophieSignup', 'true');

      // This will redirect to email verification page on success
      await signup(email, accountData.password, name, businessName, {
        phone: onboardingData.contactInfo.phone,
        address: onboardingData.contactInfo.address,
        city: onboardingData.contactInfo.city,
        province: onboardingData.contactInfo.province,
        country: onboardingData.contactInfo.country,
        postalCode: onboardingData.contactInfo.postalCode,
      });
      
      // Note: Code below might not execute due to redirect in signup
      // But we update state just in case logic changes
      setOnboardingData({ ...onboardingData, accountCreated: true });
      // We don't call nextStep() because we expect a redirect
    } catch (error) {
      console.error("Signup failed", error);
      // Toast is handled by AuthContext
    }
  };

  const handlePlanSelection = (planId: string) => {
    setOnboardingData({ ...onboardingData, selectedPlan: planId });
    setShowSuccess(true);
  };

  const renderStep = () => {
    if (showSuccess) {
      return <StepSuccess />;
    }

    switch (currentStep) {
      case 1:
        if (businessSubstep === 'search') {
          return <StepBusinessSearch onSelect={handleBusinessSelect} />;
        } else if (businessSubstep === 'confirm' && onboardingData.selectedBusiness) {
          return (
            <StepBusinessConfirm
              business={onboardingData.selectedBusiness}
              onBack={handleBusinessBack}
              onConfirm={handleBusinessConfirm}
            />
          );
        } else if (businessSubstep === 'training') {
          return <StepTrainingProgress onComplete={handleTrainingComplete} />;
        }
        return null;
      
      case 2:
        return (
          <StepContactInfo
            initialData={onboardingData.contactInfo || undefined}
            onBack={prevStep}
            onNext={handleContactInfo}
          />
        );
      
      case 3:
        return (
          <StepCreateAccount
            email={onboardingData.contactInfo?.email || ""}
            onBack={prevStep}
            onNext={handleAccountCreation}
          />
        );
      
      case 4:
        return (
          <StepChoosePlan
            onBack={prevStep}
            onSelectPlan={handlePlanSelection}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <OnboardingLayout currentStep={currentStep} totalSteps={totalSteps} hideProgress={showSuccess}>
      {renderStep()}
    </OnboardingLayout>
  );
};

export default Onboarding;
